import { Link } from "react-router-dom";
import { PLAN_FEATURES, PLAN_PRICES } from "@/lib/pricing";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LandingPricingProps {
  onSelectPlan: (plan: string) => void;
}

export function LandingPricing({ onSelectPlan }: LandingPricingProps) {
  const FREE = PLAN_FEATURES.FREE;
  const PRO = PLAN_FEATURES.PRO;
  const SCALE = PLAN_FEATURES.SCALE;

  const { data: slotsLeft = 12 } = useQuery({
    queryKey: ["founder_slots_left"],
    queryFn: async () => {
      try {
        const { count, error } = await supabase
          .from("founder_badges" as any)
          .select("*", { count: "exact", head: true });
        if (error) throw error;
        return Math.max(0, 50 - (count || 0));
      } catch (err) {
        console.error("Error fetching slots: ", err);
        return 12;
      }
    }
  });

  return (
    <section id="pricing" className="min-h-screen w-full flex items-center justify-center bg-white text-black border-b-4 border-black px-6 md:px-20 md:snap-start">
      <div className="max-w-6xl py-24 space-y-16 w-full text-center">
        
        {/* Header Section */}
        <div className="space-y-4 font-mono select-none">
          <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tight leading-none">
            Simple Pricing. Under One Wing.
          </h2>
          <p className="text-sm uppercase text-zinc-500 max-w-xl mx-auto font-black leading-relaxed">
            Choose the tier that matches your scope. Free plan forever, premium when you grow.
          </p>
        </div>

        {/* 3 Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch text-left font-mono select-none">
          
          {/* Card 1: Free */}
          <div className="p-6 border-4 border-black bg-white rounded-none flex flex-col justify-between shadow-[6px_6px_0_0_#000]">
            <div className="space-y-6">
              <div className="border-b-2 border-black pb-4">
                <span className="font-mono text-[9px] text-zinc-400 font-bold uppercase tracking-wider">FREE TIER</span>
                <h3 className="text-3xl font-black uppercase">{FREE.name}</h3>
                <p className="font-mono text-[10px] text-zinc-500 uppercase mt-1">$0 forever</p>
              </div>
              <div className="space-y-3 font-mono text-[11px] text-zinc-700 uppercase font-black">
                {FREE.highlights.map((feat) => (
                  <p key={feat}>✓ {feat}</p>
                ))}
              </div>
            </div>
            <div className="space-y-2 mt-8">
              <button
                onClick={() => onSelectPlan("starter")}
                className="w-full bg-white text-black border-2 border-black rounded-none py-3 font-black uppercase hover:bg-black hover:text-white transition-all text-xs"
              >
                Start Free
              </button>
              <p className="font-mono text-[8px] text-zinc-500 uppercase tracking-wide text-center">
                No credit card. 90s setup. Free forever.
              </p>
            </div>
          </div>

          {/* Card 2: Pro */}
          <div className="p-6 border-4 border-black bg-white rounded-none relative flex flex-col justify-between shadow-[6px_6px_0_0_#ef4444]">
            <div className="absolute top-0 right-6 transform -translate-y-1/2 border-2 border-black bg-[#FFFF00] text-black text-[9px] font-black px-2.5 py-1 uppercase tracking-wider rounded-none">
              MOST POPULAR
            </div>
            <div className="space-y-6">
              <div className="border-b-2 border-black pb-4">
                <span className="font-mono text-[9px] text-zinc-400 font-bold uppercase tracking-wider">PRO TIER</span>
                <h3 className="text-3xl font-black uppercase">${PLAN_PRICES.PRO.monthly}/MO</h3>
                <p className="font-mono text-[10px] text-zinc-500 uppercase mt-1">
                  Or ${PLAN_PRICES.PRO.annual_total}/year (Save {PLAN_PRICES.PRO.annual_discount_pct}%)
                </p>
              </div>
              <div className="space-y-3 font-mono text-[11px] text-zinc-700 uppercase font-black">
                {PRO.highlights.map((feat) => (
                  <p key={feat}>✓ {feat}</p>
                ))}
              </div>
            </div>
            <div className="space-y-2 mt-8">
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => onSelectPlan("pro_monthly")}
                  className="bg-black text-white border-2 border-black rounded-none py-3 font-black uppercase hover:bg-yellow-300 hover:text-black transition-all text-[9px] leading-none px-1"
                >
                  Monthly (${PLAN_PRICES.PRO.monthly})
                </button>
                <button
                  onClick={() => onSelectPlan("pro_annual")}
                  className="bg-[#FFFF00] text-black border-2 border-black rounded-none py-3 font-black uppercase hover:bg-black hover:text-white transition-all text-[9px] leading-none px-1"
                >
                  Annual (${PLAN_PRICES.PRO.annual_total})
                </button>
              </div>
              <p className="font-mono text-[8px] text-zinc-400 text-center uppercase tracking-wide">
                1 incident saved = 40x ROI. Cancel anytime.
              </p>
            </div>
          </div>

          {/* Card 3: Scale */}
          <div className="p-6 border-4 border-black bg-white rounded-none flex flex-col justify-between shadow-[6px_6px_0_0_#eab308] relative">
            <div className="absolute top-0 right-6 transform -translate-y-1/2 border-2 border-black bg-[#FFFF00] text-black text-[9px] font-black px-2.5 py-1 uppercase tracking-wider rounded-none text-center">
              LIMITED RUN — ONLY {slotsLeft} SEATS LEFT
            </div>
            <div className="space-y-6">
              <div className="border-b-2 border-black pb-4">
                <span className="font-mono text-[9px] text-zinc-400 font-bold uppercase tracking-wider">FOUNDER TIER</span>
                <h3 className="text-3xl font-black uppercase">${PLAN_PRICES.SCALE.monthly} ONCE</h3>
                <p className="font-mono text-[10px] text-zinc-550 uppercase mt-1">
                  Lifetime Deal Forever
                </p>
              </div>
              <div className="space-y-3 font-mono text-[11px] text-zinc-700 uppercase font-black">
                {SCALE.highlights.map((feat) => {
                  const isBadge = feat.includes("Badge");
                  return (
                    <div key={feat} className="flex flex-col gap-1">
                      <p className={isBadge ? "text-amber-600 font-black" : ""}>✓ {feat}</p>
                      {isBadge && (
                        <p className="text-[9px] font-mono lowercase tracking-normal text-zinc-500 normal-case italic pl-3 leading-normal max-w-[240px]">
                          "we will never forget the ones who trusted us early."
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2 mt-8">
              <button
                onClick={() => onSelectPlan("scale")}
                className="w-full bg-[#eab308] text-black border-2 border-black rounded-none py-3 font-black uppercase hover:bg-black hover:text-white transition-all text-xs"
              >
                Lock In Lifetime Deal →
              </button>
              <p className="font-mono text-[8px] text-zinc-400 text-center uppercase tracking-wide">
                One-time payment. Never pay again.
              </p>
            </div>
          </div>

        </div>

        {/* Bottom note */}
        <div className="pt-8 font-mono text-center">
          <p className="text-xs text-zinc-500 uppercase font-black">
            All plans include real-time monitoring.{" "}
            <Link to="/pricing" className="underline hover:text-black transition-colors">
              See full plan comparison →
            </Link>
          </p>
        </div>

      </div>
    </section>
  );
}
